import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { chambersApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Buildings, Crown, Plus, UsersThree } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_LABEL = {
    owner: { label: "Owner", icon: <Crown size={11} weight="fill" /> },
    admin: "Admin",
    associate: "Associate",
    paralegal: "Paralegal",
};

export default function Chambers() {
    const [chambers, setChambers] = useState([]);
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        try {
            const [c, i] = await Promise.all([chambersApi.list(), chambersApi.pendingInvites()]);
            setChambers(c);
            setInvites(i);
        } catch (e) {
            console.error(e);
        }
        setLoading(false);
    };

    useEffect(() => {
        load();
    }, []);

    const accept = async (invite) => {
        try {
            await chambersApi.accept(invite.id);
            toast.success("Joined chamber");
            await load();
            navigate(`/chambers/${invite.chamber_id}`);
        } catch {
            toast.error("Could not accept invite");
        }
    };

    return (
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="chambers-page">
            <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                <div className="rule-line pt-3">
                    <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1">Collaboration</div>
                    <h1 className="font-serif text-4xl tracking-tight">Chambers</h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Shared workspaces for matters, drafts and research with your team. Invited members see <b>only</b> their own personal workspace + the matters you scope to this chamber. Roles control what each member can do.
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="new-chamber-btn"><Plus size={14} /> New chamber</Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-serif">Create a chamber</DialogTitle></DialogHeader>
                        <ChamberForm onDone={(c) => { setOpen(false); load(); navigate(`/chambers/${c.id}`); }} />
                    </DialogContent>
                </Dialog>
            </div>

            {!!invites.length && (
                <div className="border border-amber-300 bg-amber-50 rounded-md p-4 mb-6" data-testid="pending-invites">
                    <div className="text-xs uppercase tracking-[0.3em] text-amber-900 mb-3 font-semibold">
                        Pending invitations ({invites.length})
                    </div>
                    <div className="space-y-2">
                        {invites.map((inv) => (
                            <div key={inv.id} className="flex items-center justify-between bg-white border border-amber-200 rounded-sm p-3">
                                <div>
                                    <div className="font-medium">{inv.chamber?.name}</div>
                                    <div className="text-xs text-muted-foreground">Role: {ROLE_LABEL[inv.role]?.label || inv.role}</div>
                                </div>
                                <Button size="sm" onClick={() => accept(inv)} data-testid={`accept-invite-${inv.id}`}>Accept</Button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-sm text-muted-foreground">Loading chambers…</div>
            ) : !chambers.length ? (
                <div className="border border-dashed border-border rounded-md p-10 text-center">
                    <Buildings size={28} className="mx-auto text-muted-foreground mb-3" weight="duotone" />
                    <div className="font-serif text-xl mb-1">No chambers yet</div>
                    <div className="text-sm text-muted-foreground">Create a chamber to share matters and collaborate.</div>
                </div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {chambers.map((c) => (
                        <Link
                            key={c.id}
                            to={`/chambers/${c.id}`}
                            data-testid={`chamber-card-${c.id}`}
                            className="border border-border rounded-md bg-white p-5 hover:border-primary/40 hover:shadow-sm transition-all"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <Buildings size={22} weight="duotone" className="text-primary" />
                                <span className="chip chip-doc inline-flex items-center gap-1">
                                    {c.my_role === "owner" && <Crown size={11} weight="fill" />}
                                    {ROLE_LABEL[c.my_role]?.label || c.my_role}
                                </span>
                            </div>
                            <div className="font-serif text-xl leading-tight">{c.name}</div>
                            {c.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{c.description}</p>}
                            <div className="text-xs text-muted-foreground flex items-center gap-1 mt-3">
                                <UsersThree size={12} weight="duotone" /> created {new Date(c.created_at).toLocaleDateString()}
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}

function ChamberForm({ initial, onDone }) {
    const [v, setV] = useState(initial || { name: "", description: "" });
    const [saving, setSaving] = useState(false);
    const save = async () => {
        if (!v.name.trim()) { toast.error("Name required"); return; }
        setSaving(true);
        try {
            const res = initial ? await chambersApi.update(initial.id, v) : await chambersApi.create(v);
            toast.success(initial ? "Saved" : "Chamber created");
            onDone?.(res);
        } catch { toast.error("Failed"); }
        setSaving(false);
    };
    return (
        <div className="space-y-3" data-testid="chamber-form">
            <L label="Chamber name"><Input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} placeholder="e.g., Singh & Co. Litigation" data-testid="chamber-name" /></L>
            <L label="Description"><Textarea rows={3} value={v.description || ""} onChange={(e) => setV({ ...v, description: e.target.value })} data-testid="chamber-desc" /></L>
            <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-background border-t border-border flex justify-end mt-2"><Button onClick={save} disabled={saving} data-testid="save-chamber-btn">{saving ? "Saving…" : initial ? "Save" : "Create"}</Button></div>
        </div>
    );
}

function L({ label, children }) {
    return <div className="space-y-1"><div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div>{children}</div>;
}

export { ChamberForm };
